import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { fetchUser } from '@/api';

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  /** The child components that will have access to the authentication context. */
  children?: ReactNode;
}

/**
 * The shape of the authentication context state.
 */
interface AuthContextType {
  /** The currently authenticated user object, or null if not logged in. */
  user: any; 
  /** Indicates whether the authentication status is still being determined. */
  loading: boolean;
  /** Indicates if the user is unauthorized but authenticated. */
  isUnauthorized: boolean;
  /** Indicates if the user has successfully logged out. */
  isLoggedOut: boolean;
  /** Function to manually update the local user state. */
  updateUser: (data: any) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Helper to get a cookie value by name.
 */
const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
};

/**
 * Helper to clear a cookie by name.
 */
const clearCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Provides authentication state to the application.
 * Automatically fetches the current user on mount.
 * 
 * @param {AuthProviderProps} props - The component props.
 * @returns {JSX.Element} The context provider component.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [isLoggedOut, setIsLoggedOut] = useState(false);

    const updateUser = (data: any) => {
        setUser((prev: any) => ({ ...prev, ...data }));
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        // Check for flags in cookies (set by backend before logout redirect)
        const unauthorizedFlash = getCookie('app_unauthorized');
        const logoutFlash = getCookie('app_logout');

        // Initialize flags based on cookies, path, or query params
        if (unauthorizedFlash === 'true' || window.location.pathname === '/unauthorized' || params.get('unauthorized') === 'true') {
            setIsUnauthorized(true);
            if (unauthorizedFlash) clearCookie('app_unauthorized');
        } else if (logoutFlash === 'true' || window.location.pathname === '/logout' || params.get('logout') === 'true') {
            setIsLoggedOut(true);
            if (logoutFlash) clearCookie('app_logout');
        }

        /**
         * Fetches user data from the API and updates state.
         */
        const getUser = async () => {
            try {
                const userData = await fetchUser();
                if (userData) {
                    setUser(userData);
                    setIsUnauthorized(false);
                } else {
                    setUser(null);
                }
            } catch (error: any) {
                console.error('Failed to fetch user:', error);
                if (error.response && error.response.status === 403) {
                    setIsUnauthorized(true);
                }
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        getUser();
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, isUnauthorized, isLoggedOut, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Custom hook to access the authentication context.
 * Must be used within an AuthProvider.
 * 
 * @throws {Error} If used outside of an AuthProvider.
 * @returns {AuthContextType} The authentication context value.
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
